/**
 * @file Utility functions for managing async function execution patterns
 * @copyright Generic code designed and created by Kyley Harris
 * @license This is generic utility code not unique to the AGWA project.
 * Created and maintained by Kyley Harris with usage rights granted to all projects.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

function isFunction(fn: unknown): fn is (...args: any[]) => any {
  return typeof fn === 'function';
}

/**
 * Will not run until calls stop for the wait period.
 * Uses a dynamic wait (number or function) and preserves `this`.
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number | (() => number)
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined;

  const getWait = (): number => (isFunction(wait) ? wait() : wait);

  return function debounced(
    this: ThisParameterType<T>,
    ...args: Parameters<T>
  ) {
    const later = () => {
      timeout = undefined;
      func.apply(this, args);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, getWait());
  };
}

/**
 * Run immediately and then refuse more executions until there has been
 * a quiet period of length `wait`. (First-call fire, then "debounce-until-idle")
 */
export function runThenDebounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number | (() => number)
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let blocked = false;

  const getWait = (): number => (isFunction(wait) ? wait() : wait);

  return function runner(this: ThisParameterType<T>, ...args: Parameters<T>) {
    const later = () => {
      timeout = undefined;
      // Quiet period elapsed; allow the next call to fire immediately.
      blocked = false;
    };

    if (!blocked) {
      func.apply(this, args);
      blocked = true;
    }

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, getWait());
  };
}

/**
 * Allows only one execution at a time (sync or async).
 * Additional calls during execution are ignored.
 * If the wrapped function returns a Promise, we block until it settles.
 * Returns the wrapped function's return value for the winning call, otherwise `undefined`.
 */
export function noParalellExecution<T extends (...args: any[]) => any>(
  func: T
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  let running = false;

  return function wrapped(
    this: ThisParameterType<T>,
    ...args: Parameters<T>
  ): ReturnType<T> | undefined {
    if (running) return undefined;

    running = true;
    try {
      const result = func.apply(this, args);
      if (result && typeof result.then === 'function') {
        // Ensure we unblock only after async completion.
        void (result as Promise<unknown>).finally(() => {
          running = false;
        });
      } else {
        // Synchronous: unblock now.
        running = false;
      }
      return result as ReturnType<T>;
    } catch (err) {
      // On error, unblock and rethrow.
      running = false;
      throw err;
    }
  };
}

/**
 * Only allows a single async execute at a time; subsequent calls while running are ignored.
 * Safe to `await`.
 */
export function noParalellExecutionAsync<
  T extends (...args: any[]) => Promise<any>,
>(func: T): (...args: Parameters<T>) => Promise<void> {
  let running = false;

  return async function wrapped(
    this: ThisParameterType<T>,
    ...args: Parameters<T>
  ): Promise<void> {
    if (running) return;
    running = true;
    try {
      await func.apply(this, args);
    } finally {
      running = false;
    }
  };
}

/**
 * Deduplicates concurrent calls: the first invocation starts the work,
 * all concurrent callers await the same Promise; once it settles, the next call will re-run.
 */
export function sharedExecute<TResult>(
  event: (...args: any[]) => Promise<TResult>
): (...args: any[]) => Promise<TResult> {
  let inflight: Promise<TResult> | undefined;

  return async (...args: any[]) => {
    if (!inflight) {
      inflight = (async () => {
        try {
          return await event(...args);
        } finally {
          // Allow future calls to trigger a fresh execution.
          inflight = undefined;
        }
      })();
    }
    return inflight;
  };
}
